import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Search,
  Shield,
  ArrowRight,
  Stethoscope,
  Globe,
  Bot,
  CreditCard,
  Monitor,
  Zap,
  Lock,
  ChevronLeft,
  ChevronRight,
  Award,
  ShieldCheck,
  Calendar,
  Code,
  CheckCircle,
  Store,
} from 'lucide-react';
import { logoBg } from '../utils/logoBg';
import { FeaturedSolutions } from '../components/home/FeaturedSolutions';

const CATEGORY_LABELS = {
  emr_systems: 'EMR Systems',
  ai_scribes: 'AI Scribes',
  administrative_tools: 'Administrative Tools',
  patient_engagement: 'Patient Engagement',
  medical_billing: 'Medical Billing',
  efaxing: 'eFaxing',
  medical_record_storage: 'Record Storage',
  specialty_tools: 'Specialty Tools',
  decision_support: 'Decision Support',
  ai_automation: 'AI Automation',
  online_booking: 'Online Booking',
  patient_administration: 'Patient Administration',
  other: 'Solution',
};

const HomeSpotlightLogo = ({ vendor, src }) => {
  const [failed, setFailed] = React.useState(false);
  const initial = (vendor.name || '?').trim().charAt(0).toUpperCase();
  return (
    <div className={`h-10 w-10 rounded-lg flex items-center justify-center overflow-hidden ${logoBg(vendor, 'bg-slate-50')}`}>
      {failed || !vendor.logo_url ? (
        <span className="text-sm font-bold text-orange-600">{initial}</span>
      ) : (
        <img
          src={src}
          alt={vendor.name}
          className="h-8 w-8 object-contain"
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [spotlight, setSpotlight] = React.useState([]);
  const [featured, setFeatured] = React.useState([]);
  const [stats, setStats] = React.useState({ solutions: null, categories: null });

  const logoSrc = (url) =>
    url && url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;

  React.useEffect(() => {
    Promise.all([
      axios.get('/vendors?limit=1000&canadian_only=false').then((r) => r.data).catch(() => []),
      axios.get('/emrs?limit=1000').then((r) => r.data).catch(() => []),
      axios.get('/categories').then((r) => r.data).catch(() => []),
    ]).then(([vendors, emrs, categories]) => {
      const all = [...(vendors || []), ...(emrs || [])];
      setStats({
        solutions: all.length || null,
        categories: new Set(all.flatMap((v) => [v.category, ...(v.categories || [])]).filter((k) => (categories || []).some((c) => c.key === k))).size || null,
      });
      // Belt: every vendor with a logo, shuffled
      const belt = (vendors || []).filter((v) => v.logo_url);
      for (let i = belt.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [belt[i], belt[j]] = [belt[j], belt[i]];
      }
      setSpotlight(belt);
      // Featured: prefer logo'd + well-reviewed, but don't require a logo
      const candidates = (vendors || []).filter((v) => v.description);
      candidates.sort((a, b) =>
        (b.logo_url ? 1 : 0) - (a.logo_url ? 1 : 0) ||
        (b.total_reviews || 0) - (a.total_reviews || 0) ||
        (b.average_rating || 0) - (a.average_rating || 0)
      );
      setFeatured(candidates.slice(0, 4));
    });
  }, []);

  const slides = [
    {
      title: 'The right technology.',
      subtitle: 'A better-running clinic.',
      breakTitle: true,
      description:
        'Discover and compare healthcare technology for Canadian medical clinics, from EMRs and AI tools to patient engagement and everyday operations.',
      secondaryText: 'Get Expert Guidance',
      secondaryLink: '/selection-guide',
      buttonText: 'Explore Marketplace',
      buttonLink: '/marketplace',
      icon: <Search className="w-16 h-16" />,
      duration: 9000,
    },
    {
      title: 'Less admin.',
      subtitle: 'More time for patients.',
      breakTitle: true,
      description:
        "Explore AI scribes, virtual receptionists and automation tools that take routine work off your team's plate, chosen with Canadian clinics in mind.",
      buttonText: 'Book a Consultation',
      buttonLink: 'https://calendar.app.google/KPh18hyBCkHBoW4CA',
      icon: <Bot className="w-16 h-16" />,
      duration: 9000,
    },
    {
      title: 'Your workflow.',
      subtitle: 'Built around your clinic.',
      breakTitle: true,
      description:
        "When off-the-shelf tools don't fit, our team builds EMR integrations and custom applications that match how your clinic actually works.",
      buttonText: 'Request a Service',
      buttonLink: '/custom-services#request-service',
      icon: <Code className="w-16 h-16" />,
      duration: 11000,
    },
  ];

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, slides[currentSlide].duration);
    return () => clearTimeout(timer);
  }, [currentSlide, slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const dragStartX = React.useRef(null);
  const onDragStart = (e) => {
    dragStartX.current = e.touches ? e.touches[0].clientX : e.clientX;
  };
  const onDragEnd = (e) => {
    if (dragStartX.current == null) return;
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const dx = endX - dragStartX.current;
    if (dx > 50) prevSlide();
    else if (dx < -50) nextSlide();
    dragStartX.current = null;
  };

  const trustStats = [
    { value: stats.solutions, label: 'Solutions listed' },
    { value: stats.categories, label: 'Categories' },
    { value: 'AI', label: 'Powered Matching' },
  ];

  const clinicFaqs = [
    { q: 'Is ClinicIQ free for clinics and providers?', a: 'Yes. Browsing, comparing, and connecting with vendors on ClinicIQ is completely free for Canadian clinics and healthcare providers.' },
    { q: 'Are the solutions Canadian-compliant?', a: 'Every listing is reviewed against Canadian healthcare standards, including PIPEDA and provincial privacy requirements, so you can shortlist with confidence.' },
    { q: 'How do I compare solutions?', a: 'Use the marketplace filters to narrow by category, province, and language, then open any listing to review features, integrations, and verified reviews side by side.' },
    { q: 'Can I get help choosing the right solution?', a: 'Yes. Book a free consultation and our team will help match solutions to your specialty, practice size, and workflow.' },
  ];

  const vendorFaqs = [
    { q: 'How do I list my solution on ClinicIQ?', a: 'Submit the application on our For Vendors page. Our team reviews each submission and typically responds within 24 hours.' },
    { q: 'Who will see my listing?', a: 'Canadian clinics and healthcare professionals who use ClinicIQ to research and compare EMRs, AI scribes, billing, telehealth, and other solutions.' },
    { q: 'What does it cost to become a partner?', a: 'Reach out through the vendor application and our partnerships team will walk you through current listing options.' },
    { q: 'Can global vendors list on ClinicIQ?', a: 'Yes. International vendors expanding into Canada can apply and be featured in our Global Vendors section, provided their solution meets Canadian healthcare and privacy requirements.' },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [...clinicFaqs, ...vendorFaqs].map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const categories = [
    { icon: <Monitor className="w-6 h-6" />, title: 'EMR Systems', description: 'OntarioMD certified EMR platforms', count: '8+ solutions', key: 'emr_systems' },
    { icon: <Bot className="w-6 h-6" />, title: 'AI Products', description: 'AI scribes, decision support, automation', count: 'Growing daily', key: 'ai_scribes' },
    { icon: <CreditCard className="w-6 h-6" />, title: 'Revenue Cycle', description: 'Billing, claims, payment solutions', count: 'Complete suite', key: 'medical_billing' },
    { icon: <Stethoscope className="w-6 h-6" />, title: 'Clinical Tools', description: 'Telehealth, monitoring, workflow tools', count: 'Comprehensive', key: 'specialty_tools' },
  ];

  const steps = [
    { n: 1, title: 'Sign Up as a Clinic', description: 'Tell us about your practice, specialty, and technology needs' },
    { n: 2, title: 'Browse & Choose', description: 'Explore pre-vetted health tech vendors tailored to Canadian healthcare' },
    { n: 3, title: 'Connect & Implement', description: 'Get expert support to integrate solutions seamlessly into your practice' },
  ];

  const certifications = [
    { icon: <Award className="w-7 h-7" />, label: 'ISO 27001', sub: 'Security Certified', color: 'text-blue-600', bg: 'bg-blue-50' },
    { icon: <ShieldCheck className="w-7 h-7" />, label: 'OntarioMD', sub: 'Certified Solutions', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { icon: <Lock className="w-7 h-7" />, label: 'PIPEDA', sub: 'Compliant', color: 'text-purple-600', bg: 'bg-purple-50' },
    { icon: <Globe className="w-7 h-7" />, label: 'Provincial', sub: 'Integrations', color: 'text-orange-600', bg: 'bg-orange-50' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ===== Hero Carousel ===== */}
      <section className="relative overflow-hidden bg-slate-900" data-testid="home-hero">
        <div
          className="relative h-[560px] sm:h-[480px] md:h-[480px] cursor-grab active:cursor-grabbing select-none"
          style={{ touchAction: 'pan-y' }}
          onTouchStart={onDragStart}
          onTouchEnd={onDragEnd}
          onMouseDown={onDragStart}
          onMouseUp={onDragEnd}
          data-testid="hero-carousel-track"
        >
          {slides.map((slide, index) => (
            <div
              key={slide.title}
              className={`absolute inset-0 flex items-start pt-10 pb-24 md:items-center md:py-0 transition-all duration-700 ease-out ${
                index === currentSlide ? 'opacity-100 translate-x-0 pointer-events-auto z-[1]' : index < currentSlide ? 'opacity-0 -translate-x-8 pointer-events-none' : 'opacity-0 translate-x-8 pointer-events-none'
              }`}
              aria-hidden={index !== currentSlide}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 md:pl-20 lg:pl-24 w-full">
                <div className="max-w-3xl text-left">
                  <div className="flex justify-start mb-4 md:mb-5">
                    <span className="h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-orange-400">
                      {React.cloneElement(slide.icon, { className: 'w-5 h-5 md:w-7 md:h-7' })}
                    </span>
                  </div>
                  <h1 className="text-[2rem] sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.1]">
                    {slide.title}{slide.breakTitle ? <br /> : ' '}
                    <span className="text-orange-400">{slide.subtitle}</span>
                  </h1>
                  <p className="mt-4 text-[15px] sm:text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
                    {slide.description}
                  </p>
                  <div className="mt-6 md:mt-7 flex flex-col sm:flex-row gap-3 justify-start">
                    {slide.buttonLink.startsWith('http') ? (
                      <a href={slide.buttonLink} target="_blank" rel="noopener noreferrer">
                        <Button size="lg" className="bg-orange-600 hover:bg-orange-500 text-white w-full sm:w-auto px-7 py-6 text-base shadow-lg shadow-orange-600/25 transition-transform hover:scale-[1.02]">
                          {slide.buttonText}
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </a>
                    ) : (
                      <Link to={slide.buttonLink} className="w-full sm:w-auto">
                        <Button size="lg" className="bg-orange-600 hover:bg-orange-500 text-white w-full sm:w-auto px-7 py-6 text-base shadow-lg shadow-orange-600/25 transition-transform hover:scale-[1.02]">
                          {slide.buttonText}
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </Link>
                    )}
                    {slide.secondaryLink && (
                      <Link to={slide.secondaryLink} className="w-full sm:w-auto" data-testid="hero-expert-guidance">
                        <Button size="lg" variant="outline" className="border-white/25 bg-white/5 text-white hover:bg-white/10 w-full sm:w-auto px-7 py-6 text-base">
                          {slide.secondaryText}
                        </Button>
                      </Link>
                    )}
                    {index === 1 && (
                      <Link to="/marketplace" className="w-full sm:w-auto">
                        <Button size="lg" variant="outline" className="border-white/25 bg-white/5 text-white hover:bg-white/10 w-full sm:w-auto px-7 py-6 text-base" data-testid={`hero-browse-${index}`}>
                          Browse Solutions
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={prevSlide} className="absolute bottom-3 right-16 md:bottom-auto md:right-auto md:left-5 md:top-1/2 md:-translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur text-white p-2.5 rounded-full transition-all z-10" aria-label="Previous slide" data-testid="hero-prev">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={nextSlide} className="absolute bottom-3 right-4 md:bottom-auto md:right-5 md:top-1/2 md:-translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur text-white p-2.5 rounded-full transition-all z-10" aria-label="Next slide" data-testid="hero-next">
          <ChevronRight className="w-5 h-5" />
        </button>

        <div className="absolute bottom-[1.1rem] left-4 sm:left-6 md:left-auto md:bottom-2 md:right-8 flex gap-2 z-10">
          {slides.map((slide, index) => (
            <button
              key={slide.title}
              onClick={() => setCurrentSlide(index)}
              className="group py-2"
              aria-label={`Go to slide ${index + 1}`}
              data-testid={`hero-dot-${index}`}
            >
              <span className={`block h-1.5 rounded-full transition-all ${index === currentSlide ? 'w-7 bg-orange-400' : 'w-2 bg-white/40 group-hover:bg-white/60'}`} />
            </button>
          ))}
        </div>
      </section>

      {/* ===== Trust Stats Strip ===== */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 divide-x divide-slate-200" data-testid="home-trust-stats">
            {trustStats.map((s) => (
              <div key={s.label} className="py-6 md:py-7 px-4 text-center">
                {s.value ? (
                  <p className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight" data-testid={`home-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>{s.value}</p>
                ) : (
                  <div className="mx-auto h-8 md:h-9 w-14 rounded-md bg-slate-100 animate-pulse" />
                )}
                <p className="text-xs md:text-sm text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Featured Vendors Belt ===== */}
      {spotlight.length > 0 && (
        <section className="py-8 md:py-10 bg-white border-b border-slate-100" data-testid="home-vendor-belt">
          <div className="marquee-mask overflow-hidden">
            <div className="marquee-track gap-3 md:gap-4" style={{ animationDuration: `${Math.max(40, spotlight.length * 3)}s` }}>
              {[...spotlight, ...spotlight].map((v, idx) => (
                <Link
                  key={`${v.id}-${idx}`}
                  to={`/vendor/${v.id}`}
                  tabIndex={idx >= spotlight.length ? -1 : undefined}
                  className="shrink-0 w-36 md:w-40 flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-4 hover:border-orange-300 hover:shadow-sm transition-all"
                  data-testid={`home-spotlight-vendor-${v.id}`}
                  title={v.name}
                  aria-hidden={idx >= spotlight.length}
                >
                  <HomeSpotlightLogo vendor={v} src={logoSrc(v.logo_url)} />
                  <span className="text-xs font-medium text-slate-700 text-center line-clamp-1 leading-tight">{v.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== Featured Solutions ===== */}
      <FeaturedSolutions fallback={featured} Logo={HomeSpotlightLogo} logoSrc={logoSrc} labels={CATEGORY_LABELS} />

      {/* ===== Categories ===== */}
      <section className="py-10 sm:py-12 md:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-8 sm:mb-12">
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-orange-600 mb-3">The Complete Stack</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Everything your practice needs
              </h2>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-slate-600 leading-relaxed">
                From foundation to optimization — browse pre-vetted Canadian healthcare technology across every category, all in one marketplace.
              </p>
              <Link to="/marketplace" className="inline-block mt-5 sm:mt-6">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-500 text-white px-6 shadow-md shadow-orange-600/20 transition-transform hover:scale-[1.02]">
                  Explore All Categories
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-3 sm:gap-4">
              <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1758691463620-188ca7c1a04f?crop=entropy&cs=srgb&fm=webp&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxkb2N0b3IlMjBsYXB0b3AlMjB0ZWxlaGVhbHRofGVufDB8fHx8MTc2MjQ4MjAxNHww&ixlib=rb-4.1.0&q=70&w=520"
                  alt="Doctor conducting telehealth consultation on laptop"
                  className="w-full h-40 sm:h-48 md:h-60 object-cover"
                  fetchpriority="high"
                  decoding="async"
                  width="520"
                  height="293"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-200">
                <img
                  src="https://images.pexels.com/photos/6129507/pexels-photo-6129507.jpeg?auto=compress&cs=tinysrgb&w=440&q=70"
                  alt="Doctors with medical staff in professional clinical environment"
                  className="w-full h-40 sm:h-48 md:h-60 object-cover"
                  fetchpriority="high"
                  decoding="async"
                  width="440"
                  height="293"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {categories.map((category, index) => (
              <Link
                key={category.key}
                to={`/marketplace?category=${category.key}`}
                className="group relative rounded-xl sm:rounded-2xl bg-white border border-slate-200 p-4 sm:p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-orange-300 transition-all duration-200"
                data-testid={`home-category-${index}`}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-orange-100 transition-colors">
                  {category.icon}
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 leading-tight">{category.title}</h3>
                <span className="inline-block mt-1.5 text-xs font-medium text-orange-600 bg-orange-50 rounded-full px-2.5 py-0.5">{category.count}</span>
                <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-slate-600 leading-relaxed">{category.description}</p>
                <ArrowRight className="absolute top-4 right-4 sm:top-6 sm:right-6 w-4 h-4 text-slate-300 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-10 sm:py-12 md:py-16 bg-white" data-testid="home-faq">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema).replace(/</g, '\\u003c') }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-600 mb-2 sm:mb-3">FAQ</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Questions? We've got answers
            </h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-slate-600 leading-relaxed">
              Everything clinics and vendors ask before getting started on ClinicIQ.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 divide-y md:divide-y-0 divide-slate-200">
            {/* Clinics */}
            <div>
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <span className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <Stethoscope className="w-5 h-5" />
                </span>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">For clinics & providers</h3>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {clinicFaqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`clinic-${i}`} className="border-slate-200">
                    <AccordionTrigger className="text-sm sm:text-base font-semibold text-slate-900 text-left hover:no-underline hover:text-orange-600 py-3 sm:py-4" data-testid={`faq-clinic-${i}`}>
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <Link to="/marketplace" className="inline-flex items-center gap-1 mt-4 sm:mt-5 text-sm font-semibold text-orange-600 hover:text-orange-700">
                Browse the marketplace <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Vendors */}
            <div className="pt-6 md:pt-0">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <span className="h-9 w-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                  <Store className="w-5 h-5" />
                </span>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">For vendors & partners</h3>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {vendorFaqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`vendor-${i}`} className="border-slate-200">
                    <AccordionTrigger className="text-sm sm:text-base font-semibold text-slate-900 text-left hover:no-underline hover:text-orange-600 py-3 sm:py-4" data-testid={`faq-vendor-${i}`}>
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <Link to="/for-vendors" className="inline-flex items-center gap-1 mt-4 sm:mt-5 text-sm font-semibold text-orange-600 hover:text-orange-700">
                Apply to list your solution <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== How It Works ===== */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-600 mb-3">Simple Process</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Get started in 3 steps</h2>
            <p className="mt-4 text-base md:text-lg text-slate-600">Up and running with ClinicIQ in minutes</p>
          </div>
          <div className="relative grid md:grid-cols-3 gap-8">
            <div className="hidden md:block absolute top-7 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-orange-200 via-orange-300 to-orange-200" />
            {steps.map((step) => (
              <div key={step.n} className="relative text-center">
                <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-orange-600 text-white flex items-center justify-center text-xl font-bold shadow-lg shadow-orange-600/25 ring-4 ring-slate-50">
                  {step.n}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Certification ===== */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 mb-4">
                <Shield className="w-4 h-4 text-orange-600" />
                <span className="text-xs font-semibold text-orange-700">Canadian Certified</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Only certified healthcare solutions
              </h2>
              <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                Every solution meets Canadian healthcare standards — ensuring compliance with PIPEDA, provincial regulations, and seamless integration with Canadian health systems.
              </p>
              <ul className="mt-6 space-y-2.5">
                {['PIPEDA & provincial privacy compliance', 'OntarioMD certified EMR platforms', 'Seamless provincial system integrations'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {certifications.map((c) => (
                <div key={c.label} className="rounded-2xl border border-slate-200 p-6 text-center hover:border-orange-300 hover:shadow-lg transition-all">
                  <div className={`w-14 h-14 mx-auto mb-3 rounded-xl ${c.bg} ${c.color} flex items-center justify-center`}>
                    {c.icon}
                  </div>
                  <div className="font-bold text-slate-900 text-sm">{c.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative overflow-hidden bg-slate-900 py-12 md:py-16">
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-orange-300 mb-5 backdrop-blur">
            <Zap className="w-3.5 h-3.5" />
            Get Started Today
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
            Ready to transform your practice?
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Join Canadian healthcare professionals who have discovered their ideal technology solutions through ClinicIQ.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto bg-orange-600 hover:bg-orange-500 text-white px-7 py-6 text-base shadow-lg shadow-orange-600/25 transition-transform hover:scale-[1.02]">
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/marketplace">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/25 bg-white/5 text-white hover:bg-white/10 px-7 py-6 text-base">
                Explore Marketplace
              </Button>
            </Link>
            <a href="https://calendar.app.google/KPh18hyBCkHBoW4CA" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 px-7 py-6 text-base">
                Book a Demo
                <Calendar className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
